import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  InsertDriveFile as FileIcon,
  OpenInNew as OpenInNewIcon,
  Payments as PaymentsIcon,
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import pagamentosService from "../services/pagamentos";
import AdicionarBoletoModal from "./AdicionarBoletoModal";
import UploadComprovantePagamentoModal from "./UploadComprovantePagamentoModal";

const PAPEL_ANALISTA = "analista";
const PAPEL_SECRETARIO = "secretario";
const PAPEL_EMPREENDEDOR = "empreendedor";

const base64ToBlobUrl = (base64, contentType) => {
  if (!base64) return null;
  try {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], {
      type: contentType || "application/octet-stream",
    });
    return URL.createObjectURL(blob);
  } catch (e) {
    return null;
  }
};

const abrirEmNovaAba = (base64, contentType) => {
  const url = base64ToBlobUrl(base64, contentType);
  if (url) {
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
};

const PagamentosSection = ({ solicitacao, papel, variant, limiteUmBoleto = false }) => {
  const theme = useTheme();
  const isPaperVariant = variant === "paper";
  const [boletos, setBoletos] = useState([]);
  const [comprovantesPorBoleto, setComprovantesPorBoleto] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalAdicionarOpen, setModalAdicionarOpen] = useState(false);
  const [modalComprovanteOpen, setModalComprovanteOpen] = useState(false);
  const [boletoSelecionado, setBoletoSelecionado] = useState(null);

  const solicitacaoId = solicitacao?.id;

  const carregarBoletos = useCallback(async () => {
    if (!solicitacaoId) return;
    try {
      setLoading(true);
      setError("");
      const lista = await pagamentosService.listarBoletosDaSolicitacao(
        solicitacaoId,
      );
      setBoletos(lista);

      const comprovantes = {};
      await Promise.all(
        lista.map(async (b) => {
          try {
            const c = await pagamentosService.getComprovanteByBoletoId(b.id);
            if (c) comprovantes[b.id] = c;
          } catch (_) {
            // silencioso por boleto
          }
        }),
      );
      setComprovantesPorBoleto(comprovantes);
    } catch (err) {
      setError(err?.message || "Erro ao carregar boletos.");
    } finally {
      setLoading(false);
    }
  }, [solicitacaoId]);

  useEffect(() => {
    carregarBoletos();
  }, [carregarBoletos]);

  const handleAbrirEnvioComprovante = (boleto) => {
    setBoletoSelecionado(boleto);
    setModalComprovanteOpen(true);
  };

  const possuiBoletoPendente = useMemo(
    () => boletos.some((b) => !b.temComprovante && !comprovantesPorBoleto[b.id]),
    [boletos, comprovantesPorBoleto],
  );

  const headerContent = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2,
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {isPaperVariant && (
          <PaymentsIcon sx={{ color: theme.palette.primary.main }} />
        )}
        <Typography
          variant="h6"
          sx={isPaperVariant ? { fontWeight: 600 } : undefined}
        >
          Pagamentos
        </Typography>
      </Box>

      {(papel === PAPEL_ANALISTA || papel === PAPEL_SECRETARIO) &&
        !(limiteUmBoleto && boletos.length > 0) && (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setModalAdicionarOpen(true)}
        >
          Solicitar Pagamento de Boleto
        </Button>
      )}
    </Box>
  );

  const renderBoletoCardVariant = (boleto, comprovante, temComprovante) => (
    <Grid item xs={12} key={boleto.id}>
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 2,
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            borderColor: alpha(theme.palette.primary.main, 0.3),
          },
        }}
      >
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FileIcon
              sx={{
                color: theme.palette.primary.main,
                fontSize: 22,
                flexShrink: 0,
              }}
            />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {boleto.descricao || "Boleto"}
              </Typography>
              {boleto.fileName && (
                <Typography variant="caption" color="text.secondary">
                  {boleto.fileName}
                </Typography>
              )}
            </Box>
            <Chip
              label={
                temComprovante
                  ? "Comprovante enviado ✓"
                  : "Aguardando comprovante"
              }
              color={temComprovante ? "success" : "warning"}
              size="small"
              sx={{ flexShrink: 0 }}
            />
            {boleto.conteudo && (
              <Tooltip title="Visualizar boleto">
                <IconButton
                  size="small"
                  aria-label="Visualizar boleto"
                  onClick={() =>
                    abrirEmNovaAba(boleto.conteudo, boleto.contentType)
                  }
                  sx={{
                    color: theme.palette.primary.main,
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: alpha(
                        theme.palette.primary.main,
                        0.1,
                      ),
                    },
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {(temComprovante || papel === PAPEL_EMPREENDEDOR) && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1.5, flexWrap: "wrap", pl: 4 }}
            >
              {temComprovante && comprovante && (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<OpenInNewIcon fontSize="small" />}
                  onClick={() =>
                    abrirEmNovaAba(
                      comprovante.conteudo,
                      comprovante.contentType,
                    )
                  }
                  disabled={!comprovante.conteudo}
                >
                  Ver Comprovante
                </Button>
              )}

              {papel === PAPEL_EMPREENDEDOR && !temComprovante && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleAbrirEnvioComprovante(boleto)}
                >
                  Enviar Comprovante de Pagamento
                </Button>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Grid>
  );

  const renderBoletoLegacy = (boleto, comprovante, temComprovante) => (
    <Box
      key={boleto.id}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="subtitle1">
            {boleto.descricao || "Boleto"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {boleto.fileName}
          </Typography>
        </Box>
        <Chip
          label={
            temComprovante
              ? "Comprovante enviado ✓"
              : "Aguardando comprovante"
          }
          color={temComprovante ? "success" : "warning"}
          size="small"
        />
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => abrirEmNovaAba(boleto.conteudo, boleto.contentType)}
          disabled={!boleto.conteudo}
        >
          Visualizar Boleto
        </Button>

        {temComprovante && comprovante && (
          <Button
            size="small"
            variant="outlined"
            color="success"
            onClick={() =>
              abrirEmNovaAba(comprovante.conteudo, comprovante.contentType)
            }
            disabled={!comprovante.conteudo}
          >
            Ver Comprovante
          </Button>
        )}

        {papel === PAPEL_EMPREENDEDOR && !temComprovante && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => handleAbrirEnvioComprovante(boleto)}
          >
            Enviar Comprovante de Pagamento
          </Button>
        )}
      </Stack>
    </Box>
  );

  const emptyContent = isPaperVariant ? (
    <Box
      sx={{
        p: 3,
        textAlign: "center",
        backgroundColor: alpha(theme.palette.divider, 0.04),
        border: `1px dashed ${alpha(theme.palette.divider, 0.4)}`,
        borderRadius: 2,
      }}
    >
      <FileIcon
        sx={{
          fontSize: 36,
          color: alpha(theme.palette.text.secondary, 0.3),
          mb: 1,
        }}
      />
      <Typography variant="body2" color="text.secondary">
        Nenhum boleto cadastrado para esta solicitação.
      </Typography>
    </Box>
  ) : (
    <Typography variant="body2" color="text.secondary">
      Nenhum boleto cadastrado para esta solicitação.
    </Typography>
  );

  const innerContent = (
    <>
      {headerContent}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {papel === PAPEL_EMPREENDEDOR && possuiBoletoPendente && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Há pagamento(s) pendente(s). Envie o comprovante de cada boleto para
          dar continuidade à solicitação.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : boletos.length === 0 ? (
        emptyContent
      ) : isPaperVariant ? (
        <Grid container spacing={1.5}>
          {boletos.map((boleto) => {
            const comprovante = comprovantesPorBoleto[boleto.id];
            const temComprovante = boleto.temComprovante || !!comprovante;
            return renderBoletoCardVariant(boleto, comprovante, temComprovante);
          })}
        </Grid>
      ) : (
        <Stack spacing={2}>
          {boletos.map((boleto) => {
            const comprovante = comprovantesPorBoleto[boleto.id];
            const temComprovante = boleto.temComprovante || !!comprovante;
            return renderBoletoLegacy(boleto, comprovante, temComprovante);
          })}
        </Stack>
      )}
    </>
  );

  const modals = (
    <>
      {(papel === PAPEL_ANALISTA || papel === PAPEL_SECRETARIO) && (
        <AdicionarBoletoModal
          open={modalAdicionarOpen}
          onClose={() => setModalAdicionarOpen(false)}
          processoSelecionado={solicitacao}
          onBoletoAdicionado={carregarBoletos}
        />
      )}

      {papel === PAPEL_EMPREENDEDOR && (
        <UploadComprovantePagamentoModal
          open={modalComprovanteOpen}
          onClose={() => {
            setModalComprovanteOpen(false);
            setBoletoSelecionado(null);
          }}
          boleto={boletoSelecionado}
          onComprovanteEnviado={carregarBoletos}
        />
      )}
    </>
  );

  if (isPaperVariant) {
    return (
      <>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          }}
        >
          {innerContent}
        </Paper>
        {modals}
      </>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      {innerContent}
      {modals}
    </Box>
  );
};

export default PagamentosSection;
export { PAPEL_ANALISTA, PAPEL_SECRETARIO, PAPEL_EMPREENDEDOR };
